/**
 * Changing what a field is.
 *
 * Its name, its type, and the properties that type carries. The three are one
 * request and one dialog because they are one question -- a field's type decides
 * which properties exist, so a picker that saved separately from the form under
 * it would have to save an incomplete field first.
 *
 * **Changing the type converts the values.** The backend rewrites every cell
 * into the new type and nulls whatever will not convert -- `5` survives becoming
 * text, `hello` does not survive becoming a number. That is not warned about
 * with a confirmation, for the same reason a paste that grows a table is not:
 * the whole change is one undo entry, and the old column is kept behind it, so
 * undo puts the values back rather than merely putting the type back. It is said
 * plainly instead, next to the picker that does it.
 *
 * **Two tabs**, because two of a field's settings are about the column rather
 * than about the values: whether the database indexes it and whether it refuses
 * duplicates. Both are rarely touched and neither belongs beside the name, so
 * they are behind "Advanced" -- which means a save blocked by something on the
 * other tab has to open the tab holding it, or the button does nothing and says
 * nothing.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import type { Field, FieldTypeName } from '@/api/types'
import { ApiError } from '@/api/errors'
import {
  draftFromField,
  fieldUpdateBody,
  offeredFieldTypes,
  readFieldProperties,
  type FieldDraft,
} from '../fields/config'
import {
  canChangeConstraints,
  hasDefaultValue,
  CAN_BE_INDEXED,
  CAN_BE_UNIQUE,
} from '../fields/constraints'
import { FieldConfigForm } from '../fields/ui/forms'
import { Toggle } from '../fields/ui/forms/controls'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { Textarea } from '@/shared/ui/textarea'
import { useCreateField, useUpdateField } from './field-mutations'

/**
 * A field that does not exist yet, shaped like one that does.
 *
 * Creating a field and retyping one are the same question -- the type decides
 * which properties there are -- so the dialog asks it once, of a blank. Every
 * reader below falls back to a default for a key that is not here, which is why
 * this can be as thin as it is: `readFieldProperties` defaults anything absent,
 * `offeredFieldTypes` offers everything to a field that is not primary, and the
 * constraint rules answer freely for a field with no values in it yet.
 */
function blankField(tableId: number): Field {
  return {
    id: 0,
    table_id: tableId,
    name: '',
    type: 'text',
    order: 0,
    primary: false,
    read_only: false,
    immutable_type: false,
    immutable_properties: false,
  } as Field
}

export function FieldDialog({
  tableId,
  viewId,
  field,
  onCreated,
  onClose,
}: {
  tableId: number
  /**
   * The view the field is being edited from.
   *
   * Read for one thing only: a field becoming an autonumber is numbered in the
   * order of a view, and the order somebody is looking at is what they mean.
   */
  viewId?: number
  /** The field being changed, or `null` to make one. */
  field: Field | null
  /**
   * Handed the field that was made, so a caller who asked for it *somewhere in
   * particular* can put it there. Nothing to do with changing one.
   */
  onCreated?: (made: Field) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const update = useUpdateField(tableId)
  const create = useCreateField(tableId)

  // Built once: a blank rebuilt on every render would reset the form under
  // whoever is typing into it.
  const [base] = useState<Field>(() => field ?? blankField(tableId))
  const making = field === null

  const [draft, setDraft] = useState<FieldDraft>(() => draftFromField(base))
  const [refused, setRefused] = useState<string | null>(null)
  const [tab, setTab] = useState('basic')

  const trimmed = draft.name.trim()
  const offered = offeredFieldTypes(base)
  // Never when making one: there are no values to convert.
  const converting = !making && draft.type !== base.type

  const canBeUnique = CAN_BE_UNIQUE[draft.type] && canChangeConstraints(base)
  const canBeIndexed = CAN_BE_INDEXED[draft.type]
  // Not a reason to disable the switch: turning the rule *off* is the fix, so
  // the control that does it has to keep working. It blocks the save instead.
  const clashesWithDefault =
    canBeUnique && draft.unique && hasDefaultValue(draft.type, draft.properties)

  function changeType(type: FieldTypeName): void {
    setDraft((held) => ({
      ...held,
      type,
      // The draft first and the field behind it: a format just chosen survives
      // a change between two types that both have one, and anything the new
      // type has no counterpart for is read from the field or defaulted rather
      // than carried over as a key it does not accept.
      properties: readFieldProperties(base, type, held.properties),
    }))
  }

  function onSave(event: React.FormEvent): void {
    event.preventDefault()
    // The rules are here rather than on a disabled button: a control that will
    // not work and will not say why is worse than one that explains itself when
    // it is pressed. Each one opens the tab it is written on, because a message
    // behind a tab nobody is looking at is a button that does nothing.
    if (trimmed === '') {
      setTab('basic')
      return
    }
    if (clashesWithDefault) {
      setTab('advanced')
      return
    }

    setRefused(null)
    const body = fieldUpdateBody(base, { ...draft, name: trimmed }, { viewId })
    const handlers = {
      onSuccess: onClose,
      onError: (error: unknown) => {
        // Shown here rather than as a toast, because it is about what is in
        // the boxes: a formula the backend refuses is refused in its own
        // words, and those words only make sense next to the expression.
        setRefused(
          error instanceof ApiError
            ? error.userMessage().description
            : t('field.saveFailed'),
        )
        if (!(error instanceof ApiError)) toast.error(t('field.saveFailed'))
      },
    }

    if (making) {
      create.mutate(body, {
        ...handlers,
        onSuccess: (made) => {
          onCreated?.(made)
          onClose()
        },
      })
      return
    }
    update.mutate({ fieldId: base.id, changes: body }, handlers)
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={onSave} noValidate className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t(making ? 'field.create' : 'field.edit')}</DialogTitle>
            <DialogDescription>
              {making ? t('field.createDescription') : base.name}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full">
              <TabsTrigger value="basic">{t('field.basic')}</TabsTrigger>
              <TabsTrigger value="advanced">{t('field.advanced')}</TabsTrigger>
            </TabsList>

            <TabsContent
              value="basic"
              className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-1"
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="field-name">{t('field.name')}</Label>
                <Input
                  id="field-name"
                  autoFocus
                  value={draft.name}
                  onChange={(event) => {
                    setDraft((held) => ({ ...held, name: event.target.value }))
                  }}
                />
                {trimmed === '' && (
                  <p className="text-sm text-destructive">{t('field.nameRequired')}</p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="field-type">{t('field.type')}</Label>
                <NativeSelect
                  id="field-type"
                  className="w-full"
                  value={draft.type}
                  // A field a data sync owns is described by whatever it syncs
                  // from, and the backend refuses to be told otherwise.
                  disabled={base.immutable_type || base.immutable_properties}
                  onChange={(event) => {
                    changeType(event.target.value as FieldTypeName)
                  }}
                >
                  {offered.map((type) => (
                    <NativeSelectOption key={type} value={type}>
                      {t(`fieldType.${type}`)}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {converting && (
                  <p className="text-sm text-muted-foreground">{t('field.converting')}</p>
                )}
                {base.immutable_type && (
                  <p className="text-sm text-muted-foreground">
                    {t('field.immutableType')}
                  </p>
                )}
              </div>

              {!base.immutable_properties && (
                <FieldConfigForm
                  type={draft.type}
                  properties={draft.properties}
                  field={base}
                  tableId={tableId}
                  name={trimmed}
                  onChange={(changes) => {
                    setDraft((held) => ({
                      ...held,
                      properties: { ...held.properties, ...changes },
                    }))
                  }}
                />
              )}

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="field-description">{t('field.description')}</Label>
                <Textarea
                  id="field-description"
                  rows={2}
                  value={draft.description}
                  onChange={(event) => {
                    setDraft((held) => ({ ...held, description: event.target.value }))
                  }}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="field-ai-description">{t('field.aiDescription')}</Label>
                <Textarea
                  id="field-ai-description"
                  rows={2}
                  placeholder={draft.description}
                  value={draft.aiDescription}
                  onChange={(event) => {
                    setDraft((held) => ({ ...held, aiDescription: event.target.value }))
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {t('field.aiDescriptionHint')}
                </p>
              </div>
            </TabsContent>

            <TabsContent
              value="advanced"
              className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-1"
            >
              <div className="flex flex-col gap-1.5">
                <Toggle
                  id="field-unique"
                  label={t('field.unique')}
                  hint={t('field.uniqueHint')}
                  checked={canBeUnique && draft.unique}
                  disabled={!canBeUnique}
                  onChange={(unique) => {
                    setDraft((held) => ({ ...held, unique }))
                  }}
                />
                {!CAN_BE_UNIQUE[draft.type] && (
                  <p className="text-sm text-muted-foreground">
                    {t('field.uniqueUnsupported', {
                      type: t(`fieldType.${draft.type}`),
                    })}
                  </p>
                )}
                {CAN_BE_UNIQUE[draft.type] && !canChangeConstraints(base) && (
                  <p className="text-sm text-muted-foreground">
                    {t('field.uniqueImmutable')}
                  </p>
                )}
                {clashesWithDefault && (
                  <p className="text-sm text-destructive">{t('field.uniqueClash')}</p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Toggle
                  id="field-indexed"
                  label={t('field.indexed')}
                  hint={t('field.indexedHint')}
                  checked={canBeIndexed && draft.indexed}
                  disabled={!canBeIndexed}
                  onChange={(indexed) => {
                    setDraft((held) => ({ ...held, indexed }))
                  }}
                />
                {!canBeIndexed && (
                  <p className="text-sm text-muted-foreground">
                    {t('field.indexedUnsupported', {
                      type: t(`fieldType.${draft.type}`),
                    })}
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {refused !== null && <p className="text-sm text-destructive">{refused}</p>}

          <DialogFooter>
            <Button type="submit" disabled={update.isPending}>
              {t('field.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
